import Colors, { shadeColor } from '@/constants/Colors'
import { isMobile, isWeb } from '@/constants/Layout'
import { TextH2 } from '@elements'
import { FontAwesome5 } from '@expo/vector-icons'
import { AlgoliaGeoDoc } from '@models/Algolia'
import { ShortCoord } from '@models/Coordinate'
import { memo } from 'react'
import { GestureResponderEvent, Pressable, StyleSheet, View } from 'react-native'
import { MapMarkerProps, MarkerPressEvent } from 'react-native-maps'

/** the event types we're expecting to receive on marker press */
type MultiplatformMarkerPressEvent = GestureResponderEvent | MarkerPressEvent

/** @param e in the mobile library it gets a MarkerPressEvent. In web it gets a regular GestureResponderEvent */
type MultiplatformMarkerOnpress = (e: MultiplatformMarkerPressEvent) => void

export const isMobileMarkerPress = (evt: MultiplatformMarkerPressEvent): evt is MarkerPressEvent => {
  return 'action' in evt.nativeEvent && evt.nativeEvent.action === 'marker-press'
}

export const isWebMarkerPress = (evt: MultiplatformMarkerPressEvent): evt is GestureResponderEvent => {
  return 'pageX' in evt.nativeEvent
}

/** Type used in the screen components to track the state of the selected marker
 * - The x and y numbers are used by the map popup (which shows on marker press) to help in its positioning calculation in relation to the map component as container
 */
export type SelectedMarker = {
  x: number | undefined
  y: number | undefined
  /** The geo documents at the given marker */
  docs: AlgoliaGeoDoc[]
} | null

/**
 * The props for the main marker component which is used in both platforms from the platform-specific file.
 *
 * ShortCoord is here because it is a requirement for the web library to correctly position the coords
 * https://github.com/google-map-react/google-map-react/blob/HEAD/API.md
 */
export type MarkerProps = {
  label: string
  isGrayDot?: boolean
  isHovered?: boolean
  onHoverIn?: () => void
  onHoverOut?: () => void
  onPress: MultiplatformMarkerOnpress
} & Pick<MapMarkerProps, 'zIndex' | 'style'> &
  ShortCoord

export const iconSize = 32 // Size of the icon

export const dotSize = 15

type MarkerContentProps = Partial<
  Pick<MarkerProps, 'isGrayDot' | 'isHovered' | 'zIndex' | 'label' | 'style' | 'onPress' | 'onHoverIn'>
>

/** This subcomponent is a child of the main platform-specific marker component, and is the common code for both platforms */
export const MarkerContent = memo(function MarkerContent({
  isGrayDot,
  isHovered,
  zIndex,
  label,
  style,
  onPress,
  onHoverIn,
}: MarkerContentProps) {
  const iconColor = isHovered ? shadeColor(Colors.blue, 1.7) : Colors.blue

  return (
    <Pressable onHoverIn={onHoverIn} onPress={onPress} style={[styles.pressablePosition, { zIndex }]}>
      {!isGrayDot ? (
        <View style={[styles.iconContainer, style]}>
          <FontAwesome5 name="map-marker" color={iconColor} size={iconSize} />
          <View style={styles.textOverlay}>
            <TextH2 color={Colors.white} center size={isHovered ? 15 : 14} type={isHovered ? 'bold' : undefined}>
              {label}
            </TextH2>
          </View>
        </View>
      ) : (
        <View style={[styles.dotMarker, isHovered && styles.dotIsHovered, style]} />
      )}
    </Pressable>
  )
})

export const styles = StyleSheet.create({
  dotMarker: {
    position: isWeb ? 'absolute' : undefined,
    top: isWeb ? -7 : undefined,
    left: isWeb ? -7 : undefined,
    borderRadius: 10,
    borderColor: Colors.white,
    borderWidth: 2,
    width: dotSize,
    height: dotSize,
    backgroundColor: Colors.shades[300],
  },
  dotIsHovered: {
    backgroundColor: Colors.shades[500],
    width: dotSize + 3,
    height: dotSize + 3,
    top: isMobile ? 0 : undefined, // if mobile set the top to 0 so that the dot is not cutted off (only appearing part off)
    left: isMobile ? 0 : undefined, // if mobile set the left to 0 so that the dot is not cutted off (only appearing part off)
  },
  textOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: Colors.transparent,
    alignItems: 'center',
    justifyContent: 'center',
    top: -4,
  },
  iconContainer: {
    position: isWeb ? 'absolute' : undefined,
    top: isWeb ? 0 : -4,
    left: 0,
    height: isWeb ? iconSize + 2 : iconSize + 12,
    width: iconSize,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressablePosition: {
    position: isWeb ? 'absolute' : undefined,
    top: isWeb ? -20 : undefined,
    left: isWeb ? -24 : undefined,
  },
})
